import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { assertNotNil } from './assertions'
import { formatPropertyAddress } from './visitLocation'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * When the work was done, shared by the visit report, the invoice and its
 * emails: the scheduled times, which the office keeps correct. startedAt and
 * completedAt are not used, as completing a visit in the ERP afterwards stamps
 * the click time.
 */
export function getVisitPerformedRange(visit: {
  startTime?: string | null
  endTime?: string | null
}) {
  const start = new Date(assertNotNil(visit.startTime, 'Visit has no start'))
  const end = new Date(
    Math.max(start.getTime(), new Date(visit.endTime ?? start).getTime()),
  )
  return {
    start,
    end,
    label: formatVisitDateRange(start, end),
    /** Stripe invoice item period, in unix seconds */
    period: { start: dayjs(start).unix(), end: dayjs(end).unix() },
  }
}

/** "Työ suoritettu 3.–4.9.2026, Katu 1, 33100 Tampere", or without the address */
export function formatWorkPerformed({
  label,
  address,
}: {
  label: string
  address: Parameters<typeof formatPropertyAddress>[0] | null | undefined
}): string {
  const formatted = address && formatPropertyAddress(address)
  return formatted
    ? `Työ suoritettu ${label}, ${formatted}`
    : `Työ suoritettu ${label}`
}

/**
 * "9.9.2026", "3.–4.9.2026", "30.9.–2.10.2026" or "30.12.2026–2.1.2027", in
 * Helsinki time: Lambda runs in UTC, where an early-morning visit falls on the
 * previous day.
 */
export function formatVisitDateRange(startDate: Date, endDate: Date): string {
  const start = dayjs(startDate).tz('Europe/Helsinki')
  const end = dayjs(endDate).tz('Europe/Helsinki')

  if (start.isSame(end, 'day')) return start.format('D.M.YYYY')
  if (start.isSame(end, 'month')) {
    return `${start.format('D.')}–${end.format('D.M.YYYY')}`
  }
  if (start.isSame(end, 'year')) {
    return `${start.format('D.M.')}–${end.format('D.M.YYYY')}`
  }
  return `${start.format('D.M.YYYY')}–${end.format('D.M.YYYY')}`
}
